import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Menu, X } from "lucide-react";
import { APP_NAME } from "@/lib/branding";
import { BrandLogo } from "@/components/BrandLogo";
import { cn } from "@/lib/utils";

const NAV = [
  { label: "Fonctionnalités", href: "#modules" },
  { label: "Personas", href: "#personas" },
  { label: "Product tour", href: "#tour" },
  { label: "Tarifs", href: "#tarifs" },
  { label: "FAQ", href: "#faq" },
];

export default function WelcomeHeader() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const on = () => setScrolled(window.scrollY > 12);
    on();
    window.addEventListener("scroll", on, { passive: true });
    return () => window.removeEventListener("scroll", on);
  }, []);

  return (
    <header
      className={cn(
        "fixed top-0 inset-x-0 z-50 transition-all duration-300",
        scrolled
          ? "backdrop-blur-xl bg-white/85 border-b border-slate-200/80 shadow-[0_1px_0_rgba(15,23,42,0.04)]"
          : "backdrop-blur-md bg-white/40 border-b border-transparent",
      )}
      data-testid="welcome-header"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-8 h-16 flex items-center justify-between">
        <Link
          to="/welcome"
          className="flex items-center gap-2.5 group"
          data-testid="welcome-header-logo"
        >
          <BrandLogo variant="icon" className="h-8 w-8" />
          <span className="font-bold tracking-tight text-[15px] text-[#0B0F17]">
            {APP_NAME}
          </span>
        </Link>

        <nav className="hidden lg:flex items-center gap-1" aria-label="Principal">
          {NAV.map((item) => (
            <a
              key={item.href}
              href={item.href}
              className="px-3 py-2 text-[13px] font-medium text-slate-600 hover:text-[#0B0F17] rounded-md transition-colors"
              data-testid={`welcome-nav-${item.href.slice(1)}`}
            >
              {item.label}
            </a>
          ))}
        </nav>

        <div className="hidden lg:flex items-center gap-2">
          <Link
            to="/login"
            className="px-3.5 py-2 text-[13px] font-semibold text-slate-700 hover:text-[#0B0F17] rounded-md transition-colors"
            data-testid="welcome-nav-login-link"
          >
            Se connecter
          </Link>
          <Link
            to="/register?plan=business"
            className="px-4 py-2 text-[13px] font-semibold text-white bg-[#0B0F17] hover:bg-[#1E293B] rounded-md transition-all shadow-sm hover:shadow-md"
            data-testid="welcome-nav-signup-button"
          >
            Essayer gratuitement
          </Link>
        </div>

        <button
          className="lg:hidden p-2 -mr-2 text-slate-700"
          onClick={() => setOpen(!open)}
          aria-label="Menu"
          data-testid="welcome-mobile-toggle"
        >
          {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
        </button>
      </div>

      {open && (
        <div className="lg:hidden border-t border-slate-200 bg-white/95 backdrop-blur-xl">
          <div className="px-6 py-4 space-y-1">
            {NAV.map((item) => (
              <a
                key={item.href}
                href={item.href}
                onClick={() => setOpen(false)}
                className="block px-3 py-2.5 text-sm font-medium text-slate-700 rounded-md hover:bg-slate-50"
              >
                {item.label}
              </a>
            ))}
            <div className="pt-3 mt-3 border-t border-slate-100 flex gap-2">
              <Link
                to="/login"
                className="flex-1 text-center px-4 py-2.5 text-sm font-semibold text-slate-700 border border-slate-200 rounded-md"
              >
                Se connecter
              </Link>
              <Link
                to="/register?plan=business"
                className="flex-1 text-center px-4 py-2.5 text-sm font-semibold text-white bg-[#0B0F17] rounded-md"
              >
                Essayer
              </Link>
            </div>
          </div>
        </div>
      )}
    </header>
  );
}
